import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { formatDistanceToNow } from 'date-fns'
import {
  HiOutlineBell,
  HiOutlineCheck,
  HiOutlineCheckCircle,
  HiOutlineTrash,
  HiArrowPath,
  HiOutlineFunnel,
  HiOutlineMagnifyingGlass,
  HiOutlineClock,
  HiOutlineChevronLeft,
  HiOutlineChevronRight,
  HiOutlineChevronDoubleLeft,
  HiOutlineChevronDoubleRight,
  HiOutlineXMark,
} from 'react-icons/hi2'
import useOvacsNotifications from '../../hooks/useOvacsNotifications'
import { ovacsNotificationAPI } from '../../services/api'
import { useToast } from '../../components/common/ToastContainer'
import {
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  IconButton,
  Input,
  PageHeader,
  Select,
  Spinner,
} from '../../components/ui'

const ICONS = {
  message: '💬', broadcast: '📢', mention: '@',
  connection_request: '🔗', connection_accepted: '✅', connection_rejected: '❌',
  invitation_sent: '📧', invitation_accepted: '🎉', role_updated: '🔄',
  document_uploaded: '📄', document_shared: '📤', document_updated: '📝', document_comment: '💬',
  case_created: '📋', case_updated: '📝', case_assigned: '👤',
  session_created: '📅', session_updated: '📅', session_reminder: '⏰',
  system: '⚙️', announcement: '📣', payment_reminder: '💳',
  subscription_expiring: '⚠️', subscription_expired: '🚫',
  contact_message: '📬', support_reply: '💬',
}

const PRIORITY_TONE = {
  low: 'neutral',
  medium: 'info',
  high: 'warning',
  urgent: 'danger',
}

export default function OvacsNotificationsPage() {
  const { t } = useTranslation()
  const toast = useToast()
  const {
    isConnected,
    markAsRead,
    markAsUnread,
    bulkAction,
    deleteNotification,
  } = useOvacsNotifications(toast)

  const [notifications, setNotifications] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedIds, setSelectedIds] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [unreadCount, setUnreadCount] = useState(0)
  const pageSize = 20

  const [filters, setFilters] = useState({
    notification_type: '',
    priority: '',
    is_read: '',
    search: '',
  })

  async function fetchNotifications(page = 1) {
    try {
      setLoading(true)
      const params = { page, page_size: pageSize, ...filters }
      Object.keys(params).forEach(k => { if (params[k] === '') delete params[k] })
      const response = await ovacsNotificationAPI.getNotifications(params)
      setNotifications(response.data.results || [])
      setTotalCount(response.data.count || 0)
      setUnreadCount(response.data.unread_count || 0)
      setTotalPages(Math.ceil((response.data.count || 0) / pageSize))
      setCurrentPage(page)
    } catch {
      toast?.error?.(t('notifications.fetchFailed'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchNotifications(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters])

  const toggleSelection = (id) => setSelectedIds(prev =>
    prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id])
  const toggleSelectAll = () => setSelectedIds(
    selectedIds.length === notifications.length ? [] : notifications.map(n => n.id))

  const handleBulkMarkRead = async () => {
    if (!selectedIds.length) return
    try {
      await bulkAction(selectedIds, 'mark_read')
      toast?.success?.(t('notifications.bulk.markReadSuccess', { count: selectedIds.length }))
      setSelectedIds([]); fetchNotifications(currentPage)
    } catch { toast?.error?.(t('notifications.bulk.markReadFailed')) }
  }
  const handleBulkMarkUnread = async () => {
    if (!selectedIds.length) return
    try {
      await bulkAction(selectedIds, 'mark_unread')
      toast?.success?.(t('notifications.bulk.markUnreadSuccess', { count: selectedIds.length }))
      setSelectedIds([]); fetchNotifications(currentPage)
    } catch { toast?.error?.(t('notifications.bulk.markUnreadFailed')) }
  }
  const handleBulkDelete = async () => {
    if (!selectedIds.length) return
    if (!window.confirm(t('notifications.bulk.deleteConfirm', { count: selectedIds.length }))) return
    try {
      await bulkAction(selectedIds, 'delete')
      toast?.success?.(t('notifications.bulk.deleteSuccess', { count: selectedIds.length }))
      setSelectedIds([]); fetchNotifications(currentPage)
    } catch { toast?.error?.(t('notifications.bulk.deleteFailed')) }
  }
  const handleMarkRead = async (id) => {
    try { await markAsRead(id); fetchNotifications(currentPage) }
    catch { toast?.error?.(t('notifications.bulk.markReadFailed')) }
  }
  const handleMarkUnread = async (id) => {
    try { await markAsUnread(id); fetchNotifications(currentPage) }
    catch { toast?.error?.(t('notifications.bulk.markUnreadFailed')) }
  }
  const handleDelete = async (id) => {
    if (!window.confirm(t('notifications.actions.deleteConfirm'))) return
    try {
      await deleteNotification(id)
      toast?.success?.(t('notifications.actions.deleteSuccess'))
      fetchNotifications(currentPage)
    } catch { toast?.error?.(t('notifications.bulk.deleteFailed')) }
  }

  const handleFilterChange = (key, value) => setFilters(prev => ({ ...prev, [key]: value }))
  const clearFilters = () => setFilters({ notification_type: '', priority: '', is_read: '', search: '' })
  const hasActiveFilters = !!(filters.search || filters.notification_type || filters.priority || filters.is_read)
  const activeFilterCount = Object.values(filters).filter(Boolean).length

  const formatDate = (s) => {
    try { return formatDistanceToNow(new Date(s), { addSuffix: true }) } catch { return s }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={HiOutlineBell}
        iconTone="brand"
        title={t('notifications.title')}
        subtitle={
          <>
            {t('notifications.totalUnread', { total: totalCount, unread: unreadCount })}
            {isConnected && <span className="ms-2 text-success font-medium">· {t('common.live')}</span>}
          </>
        }
        actions={
          <Button
            variant="secondary"
            leadingIcon={HiArrowPath}
            onClick={() => fetchNotifications(currentPage)}
            loading={loading}
          >
            {t('common.refresh')}
          </Button>
        }
      />

      <Card padding="md">
        <div className="flex items-center gap-2 mb-3">
          <HiOutlineFunnel className="w-4 h-4 text-ink-muted" />
          <h3 className="text-sm font-semibold text-ink-primary">{t('notifications.filtersTitle')}</h3>
          {hasActiveFilters && <Badge tone="brand" size="sm">{activeFilterCount}</Badge>}
          {hasActiveFilters && (
            <Button variant="link" size="sm" onClick={clearFilters} className="ms-auto">
              {t('common.clearAll')}
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <Input
            leadingIcon={HiOutlineMagnifyingGlass}
            placeholder={t('notifications.searchPlaceholder')}
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            trailingAction={
              filters.search ? (
                <IconButton
                  icon={HiOutlineXMark}
                  aria-label={t('common.clearSearch')}
                  variant="ghost"
                  size="sm"
                  onClick={() => handleFilterChange('search', '')}
                />
              ) : null
            }
          />
          <Select
            value={filters.notification_type}
            onChange={(e) => handleFilterChange('notification_type', e.target.value)}
          >
            <option value="">{t('notifications.filters.allTypes')}</option>
            <option value="message">{t('notifications.filters.messages')}</option>
            <option value="broadcast">{t('notifications.filters.broadcasts')}</option>
            <option value="connection_request">{t('notifications.filters.connectionRequests')}</option>
            <option value="invitation_accepted">{t('notifications.filters.invitations')}</option>
            <option value="document_uploaded">{t('notifications.filters.documents')}</option>
            <option value="case_created">{t('notifications.filters.cases')}</option>
            <option value="system">{t('notifications.filters.system')}</option>
          </Select>
          <Select
            value={filters.priority}
            onChange={(e) => handleFilterChange('priority', e.target.value)}
          >
            <option value="">{t('notifications.filters.allPriorities')}</option>
            <option value="urgent">{t('notifications.filters.urgent')}</option>
            <option value="high">{t('notifications.filters.high')}</option>
            <option value="medium">{t('notifications.filters.medium')}</option>
            <option value="low">{t('notifications.filters.low')}</option>
          </Select>
          <Select
            value={filters.is_read}
            onChange={(e) => handleFilterChange('is_read', e.target.value)}
          >
            <option value="">{t('notifications.filters.all')}</option>
            <option value="false">{t('notifications.filters.unread')}</option>
            <option value="true">{t('notifications.filters.read')}</option>
          </Select>
        </div>
      </Card>

      {selectedIds.length > 0 && (
        <Card padding="sm" className="bg-brand-soft/40 border-brand/30">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <Badge tone="brand">{t('common.selected', { count: selectedIds.length })}</Badge>
            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="secondary" size="sm" leadingIcon={HiOutlineCheck} onClick={handleBulkMarkRead}>
                {t('notifications.bulk.markRead')}
              </Button>
              <Button variant="secondary" size="sm" leadingIcon={HiOutlineCheckCircle} onClick={handleBulkMarkUnread}>
                {t('notifications.bulk.markUnread')}
              </Button>
              <Button variant="danger" size="sm" leadingIcon={HiOutlineTrash} onClick={handleBulkDelete}>
                {t('notifications.bulk.delete')}
              </Button>
            </div>
          </div>
        </Card>
      )}

      <Card padding="none">
        <div className="px-4 sm:px-5 py-2.5 border-b border-border-subtle bg-muted/40">
          <Checkbox
            checked={selectedIds.length === notifications.length && notifications.length > 0}
            onChange={toggleSelectAll}
            disabled={loading || notifications.length === 0}
          >
            {t('common.selectAll')}
          </Checkbox>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <Spinner size="lg" className="mb-3" />
            <p className="text-sm text-ink-muted">{t('notifications.loading')}</p>
          </div>
        ) : notifications.length === 0 ? (
          <EmptyState
            icon={HiOutlineBell}
            title={t('notifications.emptyTitle')}
            description={t('notifications.emptyDescription')}
          />
        ) : (
          <ul className="divide-y divide-border-subtle">
            {notifications.map((n) => (
              <NotificationRow
                key={n.id}
                notification={n}
                isSelected={selectedIds.includes(n.id)}
                onToggleSelect={() => toggleSelection(n.id)}
                onMarkRead={() => handleMarkRead(n.id)}
                onMarkUnread={() => handleMarkUnread(n.id)}
                onDelete={() => handleDelete(n.id)}
                formatDate={formatDate}
              />
            ))}
          </ul>
        )}
      </Card>

      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-ink-muted">
            {t('pagination.showing')}{' '}
            <span className="font-semibold text-ink-primary">{(currentPage - 1) * pageSize + 1}</span> –{' '}
            <span className="font-semibold text-ink-primary">{Math.min(currentPage * pageSize, totalCount)}</span>{' '}
            {t('pagination.of')} <span className="font-semibold text-ink-primary">{totalCount}</span>
          </p>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            disabled={loading}
            onChange={(p) => fetchNotifications(p)}
          />
        </div>
      )}
    </div>
  )
}

function NotificationRow({ notification, isSelected, onToggleSelect, onMarkRead, onMarkUnread, onDelete, formatDate }) {
  const { t } = useTranslation()
  const inner = (
    <div className={`flex items-start gap-3 p-4 hover:bg-muted/40 transition-colors ${!notification.is_read ? 'bg-brand-soft/30' : ''}`}>
      <div className="pt-1">
        <Checkbox
          checked={isSelected}
          onChange={(e) => { e.stopPropagation(); onToggleSelect() }}
          onClick={(e) => e.stopPropagation()}
          aria-label={t('common.selectAll')}
        />
      </div>
      <div className="text-2xl flex-shrink-0">{ICONS[notification.notification_type] || '🔔'}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <h3 className="text-sm font-semibold text-ink-primary">{notification.title}</h3>
          {notification.priority && (
            <Badge tone={PRIORITY_TONE[notification.priority] || 'info'} size="sm" className="uppercase">
              {notification.priority}
            </Badge>
          )}
          {!notification.is_read && <span className="w-2 h-2 bg-brand rounded-full" />}
        </div>
        <p className="text-sm text-ink-secondary mb-2 line-clamp-2">{notification.message}</p>
        <div className="flex items-center gap-2 text-xs text-ink-muted flex-wrap">
          {notification.sender_name && <><span>{t('notifications.from', { name: notification.sender_name })}</span><span>·</span></>}
          <span className="inline-flex items-center gap-1">
            <HiOutlineClock className="w-3 h-3" />
            {formatDate(notification.created_at)}
          </span>
          {notification.notification_type_display && (
            <><span>·</span><span>{notification.notification_type_display}</span></>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        {!notification.is_read ? (
          <IconButton
            icon={HiOutlineCheck}
            aria-label={t('notifications.actions.markRead')}
            variant="ghost"
            size="sm"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onMarkRead() }}
          />
        ) : (
          <IconButton
            icon={HiOutlineCheckCircle}
            aria-label={t('notifications.actions.markUnread')}
            variant="ghost"
            size="sm"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onMarkUnread() }}
          />
        )}
        <IconButton
          icon={HiOutlineTrash}
          aria-label={t('notifications.actions.delete')}
          variant="danger"
          size="sm"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete() }}
        />
      </div>
    </div>
  )
  return (
    <li>
      {notification.link ? <Link to={notification.link} className="block">{inner}</Link> : inner}
    </li>
  )
}

function Pagination({ currentPage, totalPages, disabled, onChange }) {
  const { t } = useTranslation()
  const maxVisible = 5
  let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2))
  let endPage = Math.min(totalPages, startPage + maxVisible - 1)
  if (endPage - startPage + 1 < maxVisible) startPage = Math.max(1, endPage - maxVisible + 1)
  const pages = []
  for (let i = startPage; i <= endPage; i++) pages.push(i)

  return (
    <div className="flex items-center gap-1">
      <IconButton icon={HiOutlineChevronDoubleLeft} aria-label={t('pagination.firstPage')} variant="ghost" size="sm" className="rtl:rotate-180" disabled={currentPage === 1 || disabled} onClick={() => onChange(1)} />
      <IconButton icon={HiOutlineChevronLeft} aria-label={t('pagination.previousPage')} variant="ghost" size="sm" className="rtl:rotate-180" disabled={currentPage === 1 || disabled} onClick={() => onChange(Math.max(1, currentPage - 1))} />
      {pages.map(i => (
        <button
          key={i}
          type="button"
          onClick={() => onChange(i)}
          disabled={disabled}
          className={`min-w-[2rem] h-8 px-2 text-sm font-medium rounded-md transition-colors disabled:opacity-50 ${
            i === currentPage ? 'bg-brand text-white' : 'text-ink-secondary hover:bg-muted'
          }`}
        >
          {i}
        </button>
      ))}
      <IconButton icon={HiOutlineChevronRight} aria-label={t('pagination.nextPage')} variant="ghost" size="sm" className="rtl:rotate-180" disabled={currentPage === totalPages || disabled} onClick={() => onChange(Math.min(totalPages, currentPage + 1))} />
      <IconButton icon={HiOutlineChevronDoubleRight} aria-label={t('pagination.lastPage')} variant="ghost" size="sm" className="rtl:rotate-180" disabled={currentPage === totalPages || disabled} onClick={() => onChange(totalPages)} />
    </div>
  )
}
